import React, { useState, useRef } from "react";
import propTypes from "prop-types";
import util from "../../../../util";
import Recaptcha from "react-google-invisible-recaptcha";

import Form, { Input, Label, Textarea } from "../../Components/SmartForm";
import BuyingInterest from "./BuyingInterest";
import PersonalInformation from "./PersonalInformation";
import Loader from "../../Components/Loading";
import Axios from "axios";
import Product from "../../Catalog/Item";

const GenericInquiryForm = ({
    category,
    categories,
    countries,
    quoteProduct,
    quoteSeller,
    onSuccess,
    hubspotLists,
    isProductAction = false,
    ...props,
}) => {
    const siteKey = process.env.RECAPTCHA_SITE_KEY || localStorage.getItem("recaptchaSiteKey");
    const recaptcha = useRef();
    const [display, setDisplay] = useState({
        isSubmitting: false,
        created: false,
        error: false,
    });
    const form = useRef();
    const displayControl = useRef();
    const action = `/inquiries/${category ? category.url_key : ""}${quoteProduct ? `?product=${quoteProduct.sku}` : ""}`;

    displayControl.current = {
        setDisplay,
    };

    const onSubmit = (recaptcha) => {
        const { regions } = countries;
        const quoteData = form.current.getValuesAsObject();

        displayControl.current.setDisplay({
            isSubmitting: true,
            created: false,
            error: false,
        });

        Axios.post(action, {
            ...quoteData,
            recaptcha,
        })
            .then((result) => {
                if (!result.data.success) {
                    throw result;
                }

                displayControl.current.setDisplay({
                    isSubmitting: false,
                    created: true,
                    error: false,
                });

                // Track the category interest in HubSpot.
                try {
                    let formId, pageName, pageURL;

                    if (quoteProduct) {
                        formId = "a2fdea91-37a5-4335-a4ba-2260eb6f6b4c";
                        pageName = "LabX as Inquiry Page";
                        pageURL = `/item/${quoteProduct.name
                            .replace(/ /gi, "-")
                            .toLowerCase()}/${quoteProduct.sku}`;
                    } else {
                        const selectedCategoryId = form.current.getValue("category");
                        const selectedCategoryName = categories.find(({id}) => id == selectedCategoryId).name;

                        formId = "ed6fe59a-8552-4118-ac7a-16624a2d4bab";
                        pageName = `QUOTE_LabX_Category: ${selectedCategoryName} (CategoryID: ${selectedCategoryId})`;
                        pageURL = action;
                    }

                    //Prepare our API calls to HubSpot
                    const hubspotAPICalls = [];

                    //Clean quote data to match hubspot data structure
                    [
                        ["firstName", "firstname"],
                        ["lastName", "lastname"],
                        ["postalCode", "postcode"],
                        ["region_id", "state"]
                    ].forEach(([oldName, newName]) => {
                        if(quoteData[oldName]){
                            quoteData[newName] = quoteData[oldName];
                            delete quoteData[oldName];
                        }
                    });
                    const activeRegionCode = Object.keys(regions).find(countryCode => countryCode == quoteData.country_id);

                    if(activeRegionCode){
                        const activeRegion = regions[activeRegionCode].find(region => region.id == quoteData.state);

                        if(activeRegion) quoteData.state = activeRegion.name;
                    }

                    const hubspotData = Object.assign({
                        hubspotCookie: document.cookie
                            .split(";")
                            .find((item) => item.match(/hubspotutk/))
                            .replace(/hubspotutk=/, "")
                            .trim(),
                        form_guid: formId,
                        pageName: pageName,
                        pageURL: pageURL,
                    }, quoteData);

                    hubspotAPICalls.push(Axios.post("/api/hubspot", hubspotData));

                    let hubspotInquiryList;

                    if(isProductAction){
                        hubspotInquiryList = hubspotLists.filter(list => /^Inquiry_/.test(list))[0];
                    } else {
                        hubspotInquiryList = hubspotLists.filter(list => /^CategoryQuote_/.test(list))[0];
                    }

                    if(hubspotInquiryList !== undefined){
                        const listId = hubspotInquiryList.match(/\d+/g)[0];

                        hubspotAPICalls.push(Axios.post("/api/hubspot/addContactToList", {
                            listId,
                            email: hubspotData.email
                        }));
                    }

                    Promise.all(hubspotAPICalls).catch(error => {
                        throw error;
                    });
                } catch (error) {
                    console.error(error);
                    // The user is not affected by whether this succeeds or not.
                    // Once the quote is created, the user should be guaranteed
                    // to get the success page.
                }

                if (onSuccess) {
                    onSuccess();
                }
            })
            .catch((error) => {
                if (!error.response) {
                    error.response = {
                        data: {
                            errors: [error.message],
                        }
                    };
                }

                const data = error.response.data;

                if (data.errors) {

                    displayControl.current.setDisplay({
                        isSubmitting: false,
                        created: false,
                        error: data.errors.join("<br />"),
                    });

                    if (data.fieldErrors) {
                        form.current.setAllErrors(data.fieldErrors);
                    }
                    if (data.fieldValues) {
                        form.current.setValues(data.fieldValues);
                    }
                    return;
                }

                displayControl.current.setDisplay({
                    isSubmitting: false,
                    created: false,
                    error: `${error.response.status}: ${error.response.text}`,
                });
            });
    };

    const validateRecaptcha = (e) => {
        e.preventDefault();
        recaptcha.current.execute();
    };

    return (display.isSubmitting
        ? (
            <Loader />
        ) : display.created
            ? (
                <div className="card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-8 col-12-sm">
                                <h1 className="title">
                                    <strong>
                                        Request for Quote Sent!
                                    </strong>
                                </h1>
                                <p>
                                    Thank you. Your request has been
                                    successfully submitted.
                                </p>
                                <p>
                                    Your Request for Quote will be
                                    shared with Sellers that match your
                                    purchasing requirements. They will
                                    contact you with product information
                                    and buying options.
                                </p>
                            </div>
                            <div className="col-md-4 col-12-sm d-flex flex-column text-center justify-content-evenly m-auto">
                                <a
                                    href={window.location}
                                    className="btn btn-outline-lx">
                                    Submit Another Quote
                                </a>
                                {category && <a
                                    href={`/${util.convertToUrlKey(category.name)}`}
                                    className="btn btn-lx">
                                    Return to { category.name }
                                </a>}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <Form
                    id="generic-inquiry"
                    action={action}
                    onSubmit={validateRecaptcha}
                    contextRef={form}
                    defaults={{
                        category: category ? category.id : "",
                    }}
                    {...props}
                >
                    {display.error && (
                        <div className="alert alert-danger">
                            <strong>Error: </strong>
                            {display.error}
                        </div>
                    )}
                    { quoteProduct ? (
                        <div className="card mb-3">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h5 className="title">Requesting Quote from Seller</h5>
                                        <div className={quoteSeller.seller_logo}>
                                            <p>
                                                {quoteSeller.seller_name}
                                            </p>
                                            <p className="card-subtitle mb-2 text-muted">
                                                {quoteSeller.seller_locality}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <Input type="hidden" name="request_type" value="quote" />
                                        <Input type="hidden" name="product_id" value={quoteProduct.sku} />
                                        <h5 className="title">Requesting Quote for Product</h5>
                                        <div className="quote-form">
                                            <div className="product-list">
                                                <Product
                                                    name={quoteProduct.name}
                                                    sku={quoteProduct.sku}
                                                    image={quoteProduct.image}
                                                    condition={quoteProduct.condition}
                                                    price={quoteProduct.price}
                                                    showSku={true}
                                                    hover={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <React.Fragment>
                            <Input type="hidden" name="request_type" value="generic" />
                            <BuyingInterest
                                className="mb-3"
                                showCategoryInput={category ? false : true }
                                categories={categories}
                            />
                        </React.Fragment>
                    )}
                    <PersonalInformation
                        className="mb-3"
                        {...countries}
                    />
                    {quoteProduct && <div className="form-group">
                        <Label
                            input="message"
                            required
                        >
                            Message
                        </Label>
                        <Textarea
                            name="message"
                            className="form-control"
                            rows={3}
                            required
                        />
                    </div>}
                    <button type="submit" className="btn highlight request-quote-submit">
                        <i className="fas fa-file-edit"></i>
                        Submit Request for Quote
                    </button>
                    <Recaptcha
                        ref={recaptcha}
                        sitekey={siteKey}
                        onResolved={onSubmit}
                    />
                </Form>
            )
    );
};

GenericInquiryForm.propTypes = {
    category: propTypes.object,
    categories: propTypes.arrayOf(propTypes.object),
    countries: propTypes.object,
    quoteSeller: propTypes.object,
    quoteProduct: propTypes.object,
    onSuccess: propTypes.func,
    hubspotLists: propTypes.array,
    isProductAction: propTypes.bool
};

export default GenericInquiryForm;
